import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, ArrowRight, Star, Clock, TrendingUp, Award, Lock, FileText, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { images, serviceTestimonials, serviceProcessSteps } from '../../mock';
import InnerPageHero from '../../components/InnerPageHero';
import InnerPageBanner from '../../components/InnerPageBanner';

const ServicePage = ({ serviceName }) => {
  const [currentTestimonial, setCurrentTestimonial] = React.useState(0);

  // Determine service type for testimonials and process
  const getServiceType = () => {
    if (serviceName.toLowerCase().includes('copyright')) return 'copyright';
    if (serviceName.toLowerCase().includes('patent')) return 'patent';
    if (serviceName.toLowerCase().includes('llc') || serviceName.toLowerCase().includes('corporation') || serviceName.toLowerCase().includes('company')) return 'company';
    if (serviceName.toLowerCase().includes('search')) return 'search';
    if (serviceName.toLowerCase().includes('amazon')) return 'amazon';
    return 'trademark';
  };

  const serviceType = getServiceType();
  const testimonials = serviceTestimonials[serviceType] || serviceTestimonials.trademark;
  const processSteps = serviceProcessSteps[serviceType] || serviceProcessSteps.trademark;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 3) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const getServiceDescription = () => {
    const descriptions = {
      'TTAB Petition': 'Expert representation for Trademark Trial and Appeal Board proceedings, including oppositions and cancellations.',
      'Clearance Search Services': 'Comprehensive trademark searches to identify potential conflicts before filing your application.',
      'Trademark Ownership Transfers': 'Seamless transfer of trademark ownership with proper documentation and USPTO filing.',
      'Managed Trademark Renewal': 'Never miss a renewal deadline with our comprehensive trademark portfolio management.',
      'Submission of Statements of Use': 'Timely filing of Statements of Use to complete your trademark registration.',
      'Support & Response for Trademark Office Actions': 'Expert handling of USPTO office actions to keep your application on track.',
      'Active Monitoring for Trademark Infringement': 'Continuous monitoring to detect and address potential trademark infringement.',
      'Support & Guidance for Trademark Cancellations': 'Professional guidance through trademark cancellation proceedings.',
      'Legal Representation for Trademark Disputes': 'Experienced legal representation for trademark disputes and litigation.',
      'Combatting Counterfeiting & Trademark Infringement': 'Aggressive enforcement actions against counterfeiters and infringers.',
      'Register Your Copyright': 'Protect your creative works with official copyright registration.',
      'Copyright Enforcement Assistance': 'Take action against copyright infringement with our enforcement services.',
      'Copyright Transfers & Licensing Support': 'Navigate copyright transfers and licensing agreements with expert guidance.',
      'Legal Representation for Copyright': 'Professional legal representation for copyright matters and disputes.',
      'Copyright Portfolio Management': 'Comprehensive management of your copyright portfolio and renewals.',
      'DMCA Compliance': 'Ensure DMCA compliance and respond effectively to takedown notices.',
      'Existing Patent Search': 'Thorough patent searches to assess novelty and patentability.',
      'Response to Patent Office Actions': 'Expert responses to USPTO patent office actions.',
      'Patent Application Submission': 'Professional preparation and filing of patent applications.',
      'Legal Representation for Patent Protection': 'Comprehensive legal representation for patent matters.',
      'LLC Formation': 'Form your Limited Liability Company with complete filing services.',
      'C Corporation Formation': 'Establish your C Corporation with professional incorporation services.',
      'S Corporation Formation': 'Create your S Corporation with proper IRS election and state filing.',
      'Non-Profit Formation': 'Form your non-profit organization with 501(c)(3) application assistance.',
      'Legal Consultation on Partnerships & LLPs': 'Expert legal advice for partnership formations and LLP structures.',
      'Due Diligence': 'Comprehensive due diligence services for business transactions.',
      'Business Immigration Law': 'Navigate business immigration matters with experienced legal guidance.',
      'IT & Commercial Law': 'Legal services for technology companies and commercial transactions.',
      'Data Protection & Privacy Law': 'Ensure compliance with data protection and privacy regulations.',
      'Amazon Brand Registry (US)': 'Register your brand with Amazon Brand Registry for enhanced protection.',
      'Business Formation Services': 'Complete business entity formation and structuring services.',
      'Company Dissolution': 'Properly dissolve your company with all required filings.',
      'Issue of Shares': 'Professional assistance with share issuance and documentation.',
      'Transfer of Shares': 'Facilitate share transfers with proper legal documentation.',
      'Certificate of Good Standing': 'Obtain certificates of good standing for your business entity.',
      'Company Name Change': 'Change your company name with proper state and federal filings.',
      'Company Renewals': 'Manage annual renewals and compliance filings for your business.',
      'Nominee Director & Secretarial Services': 'Professional nominee director and corporate secretarial services.',
      'Bookkeeping': 'Comprehensive bookkeeping services for your business.',
      'Tax Advisory & Filing': 'Expert tax planning, advisory, and filing services.',
      'CFO Services': 'Fractional CFO services for strategic financial management.'
    };
    return descriptions[serviceName] || 'Professional services to protect and grow your business.';
  };

  const getBadgeText = () => {
    if (serviceName.toLowerCase().includes('trademark')) return 'TRADEMARK SERVICES';
    if (serviceName.toLowerCase().includes('copyright')) return 'COPYRIGHT SERVICES';
    if (serviceName.toLowerCase().includes('patent')) return 'PATENT SERVICES';
    if (serviceName.toLowerCase().includes('llc') || serviceName.toLowerCase().includes('corporation')) return 'COMPANY FORMATION';
    if (serviceName.toLowerCase().includes('legal')) return 'LEGAL SERVICES';
    if (serviceName.toLowerCase().includes('amazon')) return 'BRAND REGISTRY';
    if (serviceName.toLowerCase().includes('bookkeeping') || serviceName.toLowerCase().includes('tax') || serviceName.toLowerCase().includes('cfo')) return 'FINANCIAL SERVICES';
    return 'PROFESSIONAL SERVICES';
  };

  return (
    <>
      <Helmet>
        <title>{serviceName} | Trusted Trademark Filer</title>
        <meta name="description" content={`${getServiceDescription()} Professional service from Trusted Trademark Filer with expert guidance and support.`} />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <InnerPageHero
        badge={getBadgeText()}
        title={serviceName}
        description={getServiceDescription()}
      />

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive solutions tailored to your specific needs with expert guidance every step of the way.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md">
                <Shield className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Professional Expertise
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Benefit from years of experience and deep knowledge in intellectual property and business law from our expert team.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md">
                <CheckCircle className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Personalized Service
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Get dedicated attention from case managers who understand your unique business needs and goals.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md">
                <Clock className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fast Processing
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Our efficient procedures ensure fast turnaround times without compromising on quality or attention to detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process - 3rd Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-base">HOW IT WORKS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Simple, transparent steps for your {serviceName.toLowerCase()}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center gap-4 mb-6">
                  <span className="bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white text-lg font-bold px-6 py-3 rounded-lg shadow-md">
                    {step.step}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What's Included in This Service
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive support throughout your entire journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Expert Consultation</h3>
                <p className="text-base text-gray-600">Initial consultation to understand your needs and develop a tailored strategy</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Document Preparation</h3>
                <p className="text-base text-gray-600">Professional preparation of all necessary documents and applications</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Filing & Submission</h3>
                <p className="text-base text-gray-600">Accurate filing with all required government agencies and offices</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Progress Monitoring</h3>
                <p className="text-base text-gray-600">Regular updates on your application status and any required actions</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Dedicated Support</h3>
                <p className="text-base text-gray-600">Personal case manager available to answer all your questions</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-md border border-gray-100">
              <CheckCircle className="h-6 w-6 text-[#FF4A2A] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Final Delivery</h3>
                <p className="text-base text-gray-600">Complete documentation and certificates upon successful completion</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Creative Banner */}
      <InnerPageBanner onOpenPopup={() => {}} />

      {/* Key Information Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-[#E2F8FE] rounded-full opacity-50 blur-3xl"></div>
              <img
                src={images.about}
                alt="Professional Service"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover relative z-10 border-4 border-white"
              />
            </div>

            {/* Right - Content */}
            <div className="space-y-6">
              <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2">
                <span className="text-[#FF4A2A] font-semibold text-sm">WHY THIS MATTERS</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900">
                Protect Your Business with Professional Legal Services
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Don't leave your business vulnerable to legal challenges. Our comprehensive services provide the protection and peace of mind you need to focus on growing your business.
              </p>

              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Comprehensive Protection</h3>
                    <p className="text-base text-gray-600">Full legal protection for your intellectual property and business assets.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Expert Team</h3>
                    <p className="text-base text-gray-600">Dedicated professionals with years of experience in IP and business law.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-base">Proven Results</h3>
                    <p className="text-base text-gray-600">High success rate with 500+ satisfied clients across various industries.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#E2F8FE] rounded-full px-6 py-2 mb-4">
              <span className="text-[#FF4A2A] font-semibold text-sm">COMMON QUESTIONS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to the most common questions about our services
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How long does the process take?</h3>
              <p className="text-base text-gray-600">
                The timeline varies depending on the service, but we typically complete initial filings within 24-48 hours. Full registration can take 6-12 months depending on government processing times.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What is your success rate?</h3>
              <p className="text-base text-gray-600">
                We maintain a 95% approval rate through our comprehensive research, expert preparation, and professional filing procedures. Our team's experience ensures the highest chance of success.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Do you provide ongoing support?</h3>
              <p className="text-base text-gray-600">
                Yes! We offer 24/7 customer support and dedicated case managers who guide you through every step of the process and beyond. We're here whenever you need us.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What if my application is rejected?</h3>
              <p className="text-base text-gray-600">
                While rare with our thorough preparation, if an office action is issued, we provide expert responses at no additional cost. We work diligently to address any concerns and get your application approved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Sliding Display */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-white rounded-full px-6 py-2 mb-4 shadow-md border border-[#E2F8FE]">
              <span className="text-[#FF4A2A] font-semibold text-base">TESTIMONIALS</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by businesses for {serviceName.toLowerCase()}
            </p>
          </div>

          {/* Desktop: Show 3 testimonials with slider */}
          <div className="hidden md:block relative">
            <div className="grid grid-cols-3 gap-8">
              {testimonials.slice(currentTestimonial, currentTestimonial + 3).map((testimonial, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-[#E2F8FE] rounded-xl shadow-lg p-8 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex justify-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-[#FF4A2A] fill-[#FF4A2A]" />
                    ))}
                  </div>
                  <p className="text-base text-gray-700 italic leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="font-bold text-gray-900 text-base">
                      {testimonial.name}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-12">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index * 3)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    Math.floor(currentTestimonial / 3) === index ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Mobile: Show slider */}
          <div className="md:hidden relative bg-gradient-to-br from-white to-[#E2F8FE] rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="text-center space-y-6">
              <div className="flex justify-center mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-[#FF4A2A] fill-[#FF4A2A]" />
                ))}
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div className="pt-6 border-t border-gray-200">
                <p className="font-bold text-gray-900 text-base">
                  {testimonials[currentTestimonial].name}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  {testimonials[currentTestimonial].company}
                </p>
              </div>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial ? 'w-12 bg-[#FF4A2A]' : 'w-3 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF4A2A] to-[#E63E1F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-8 max-w-lg mx-auto opacity-90">
            Let our experts handle your {serviceName.toLowerCase()} needs with professional care and attention.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-white text-[#FF4A2A] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-lg transition-all duration-300"
            >
              Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              className="border-2 border-white text-white hover:bg-white hover:text-[#FF4A2A] px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ServicePage;